import { type DropdownEntry } from "~/common/types";
import { useTranslation, type WithTranslation } from "react-i18next";
import { useMemo } from "react";

type DropdownData = {
  titles: { code: string; name: string }[];
  salutations: { code: string; name: string }[];
  industrySectors: { code: string; name: string }[];
  jobTitles: { code: string; name: string }[];
  functionalAreas: { code: string; name: string }[];
  countries: { isocode: string; name: string }[];
  customerCategories: { code: string; name: string }[];
  knowledgeAreas: { code: string; name: string }[];
  languages: { code: string; name: string }[];
};
export const useMyAccountDropdownValues = (data: DropdownData) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const sortFunc = (a: DropdownEntry<string>, b: DropdownEntry<string>) =>
      a.stringLabel!.localeCompare(b.stringLabel!);

    const titleOptions = toDropdownLabelEntries(data.titles, "code", "name", t);
    const salutationOptions = toDropdownLabelEntries(data.salutations, "code", "name", t);
    const industryOptions = toDropdownLabelEntries(data.industrySectors, "code", "name", t);
    const jobTitleOptions = toDropdownLabelEntries(data.jobTitles, "code", "name", t);
    const functionalAreaOptions = toDropdownLabelEntries(data.functionalAreas, "code", "name", t);
    const customerCategoryOptions = toDropdownLabelEntries(data.customerCategories, "code", "name", t);
    const knowledgeAreaOptions = toDropdownLabelEntries(data.knowledgeAreas, "code", "name", t);
    const languageOptions = toDropdownLabelEntries(data.languages, "code", "name", t);

    const countryOptions = toDropdownLabelEntries(data.countries, "isocode", "name", t);
    countryOptions.sort(sortFunc);

    return {
      titleOptions,
      salutationOptions,
      countryOptions,
      industryOptions,
      jobTitleOptions,
      functionalAreaOptions,
      knowledgeAreaOptions,
      customerCategoryOptions,
      languageOptions,
    };
  }, [data]);
};

const toDropdownLabelEntries = <T extends Record<string, string>>(
  items: T[],
  valueKey: keyof T,
  labelKey: keyof T,
  t: WithTranslation["t"],
): DropdownEntry<string>[] =>
  items.map((item) => ({
    label: t(item[labelKey] as unknown as string),
    stringLabel: t(item[labelKey] as unknown as string) ?? "",
    value: item[valueKey] as unknown as string,
  }));
